/**
 * EHR proband demographics drift detection for the in-progress order page.
 *
 * When a submitted order is (re)opened from an Epic SMART launch, the SPA
 * re-fetches the launched patient's demographics from the EHR (via
 * `POST /fhir/form_info` → `extractFormOrderData`). If any of the four
 * clinically-significant identity fields — name, date of birth, sex, MRN —
 * differ from what was saved on the order, the provider is offered a chance to
 * push the corrected values to the lab.
 *
 * These helpers are deliberately free of React/store/network dependencies so
 * the field-by-field comparison is unit-testable in isolation and reused by
 * both the drift banner/modal and the "send this to the lab" merge path. The
 * normalization mirrors {@link serializePatientDemographicsBaseline}: values are
 * trimmed and the DOB is compared on its `yyyy-mm-dd` date portion only, so a
 * stored value with a time component (or a differently-formatted equivalent)
 * does not read as a phantom change.
 */
import type { CanonicalPatient } from '@/ordering/canonicalOrder'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'

/** The proband identity fields compared for EHR drift. */
export type EhrDemographicsFieldId = 'name' | 'dateOfBirth' | 'sex' | 'mrn'

/** Human-readable labels for each compared field (used by the drift modal). */
export const EHR_DEMOGRAPHICS_FIELD_LABELS: Record<EhrDemographicsFieldId, string> = {
  name: 'Name',
  dateOfBirth: 'Date of birth',
  sex: 'Sex',
  mrn: 'MRN',
}

/**
 * The four proband demographic fields, normalized for comparison and merge.
 *
 * `dateOfBirth` is the `yyyy-mm-dd` date portion (empty string when unset).
 * `name` fields are trimmed. This is the canonical merge shape written back via
 * `mergePatient` when the provider accepts the EHR values.
 */
export interface NormalizedProbandDemographics {
  firstName: string
  middleName: string
  lastName: string
  dateOfBirth: string
  sex: string
  mrn: string
}

/** One field whose EHR value differs from the saved order value. */
export interface EhrDemographicsFieldDiff {
  field: EhrDemographicsFieldId
  label: string
  /** Value currently saved on the order (may be empty). */
  savedValue: string
  /** Value currently in the EHR (may be empty). */
  ehrValue: string
}

/** Result of comparing saved order demographics against the EHR. */
export interface EhrDemographicsDrift {
  /** Fields that differ, in a stable display order (name, DOB, sex, MRN). */
  changedFields: EhrDemographicsFieldDiff[]
  /** True when at least one of the four fields differs. */
  hasDrift: boolean
  /** The normalized EHR demographics (full snapshot; for reference/tests). */
  ehr: NormalizedProbandDemographics
  /**
   * Canonical-patient patch containing ONLY the fields that actually drifted.
   *
   * This is what the "send to lab" merge must apply — never the full {@link ehr}
   * snapshot. Fields the comparison skipped because the EHR value was blank are
   * intentionally absent here, so accepting the update can never overwrite a
   * good saved name / MRN / sex / DOB with an empty EHR value. A drifted `name`
   * contributes `firstName` / `middleName` / `lastName` together (middleName is
   * `null` when the EHR has no middle name, mirroring how the editor clears it).
   */
  mergePatch: EhrDemographicsMergePatch
}

/** Partial canonical-patient update carrying only the drifted fields. */
export type EhrDemographicsMergePatch = Partial<
  Pick<CanonicalPatient, 'firstName' | 'middleName' | 'lastName' | 'dateOfBirth' | 'sex' | 'mrn'>
>

function trimStr(value: string | null | undefined): string {
  return (value ?? '').trim()
}

/** Normalize a Date/string DOB to its `yyyy-mm-dd` portion.
 *
 * DOB is a date-only value. `extractFormOrderData` produces the EHR `birthDate`
 * via `new Date("yyyy-mm-dd")`, which JS parses as UTC midnight — so the date
 * portion must be read from the UTC parts. Reading LOCAL parts here shifts the
 * day back by one in negative-offset timezones (e.g. UTC-4 turns 1974-08-21
 * into 1974-08-20), which would surface as a phantom DOB "change". A plain
 * string is already the date portion, so it is trimmed and sliced as-is.
 */
function normalizeDob(value: Date | string | null | undefined): string {
  if (value == null) return ''
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return ''
    const y = value.getUTCFullYear()
    const m = String(value.getUTCMonth() + 1).padStart(2, '0')
    const d = String(value.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  return trimStr(value).slice(0, 10)
}

/** Build the normalized comparison shape from the saved canonical patient. */
export function normalizeCanonicalProbandDemographics(
  patient: CanonicalPatient | null | undefined
): NormalizedProbandDemographics {
  return {
    firstName: trimStr(patient?.firstName),
    middleName: trimStr(patient?.middleName),
    lastName: trimStr(patient?.lastName),
    dateOfBirth: normalizeDob(patient?.dateOfBirth),
    sex: trimStr(patient?.sex),
    mrn: trimStr(patient?.mrn),
  }
}

/** Build the normalized comparison shape from the EHR `form_info` patient. */
export function normalizeEhrProbandDemographics(
  patient: OrderFormInfoData['patient'] | null | undefined
): NormalizedProbandDemographics {
  return {
    firstName: trimStr(patient?.name?.firstName),
    middleName: trimStr(patient?.name?.middleName),
    lastName: trimStr(patient?.name?.lastName),
    dateOfBirth: normalizeDob(patient?.birthDate),
    sex: trimStr(patient?.sex),
    mrn: trimStr(patient?.mrn),
  }
}

/** Join the name parts into a `Last, First Middle` display string (blank parts dropped). */
function formatName(d: NormalizedProbandDemographics): string {
  const givenNames = [d.firstName, d.middleName].filter(Boolean).join(' ')
  return d.lastName && givenNames ? `${d.lastName}, ${givenNames}` : d.lastName || givenNames || ''
}

/**
 * Compare the saved order demographics against the EHR demographics.
 *
 * Only the four identity fields (name, DOB, sex, MRN) are compared. Name is
 * compared as the combined first/middle/last so a changed middle name is
 * caught, and surfaced as a single "Name" row. An EHR field that is blank is
 * NOT treated as drift when the saved value is non-blank: a missing/blank EHR
 * value almost always means the SPA simply couldn't resolve it (e.g. MRN under
 * a different identifier system) rather than an intentional clear, so blanking
 * a real saved value must never be auto-proposed to the lab.
 */
export function compareProbandDemographicsToEhr(
  savedPatient: CanonicalPatient | null | undefined,
  ehrPatient: OrderFormInfoData['patient'] | null | undefined
): EhrDemographicsDrift {
  const saved = normalizeCanonicalProbandDemographics(savedPatient)
  const ehr = normalizeEhrProbandDemographics(ehrPatient)

  const changedFields: EhrDemographicsFieldDiff[] = []
  const mergePatch: EhrDemographicsMergePatch = {}

  const savedName = formatName(saved)
  const ehrName = formatName(ehr)
  if (ehrName && ehrName !== savedName) {
    changedFields.push({
      field: 'name',
      label: EHR_DEMOGRAPHICS_FIELD_LABELS.name,
      savedValue: savedName,
      ehrValue: ehrName,
    })
    // Name drifted: apply all three name parts together. middleName is nulled
    // when the EHR has none so accepting a name change clears a stale middle.
    mergePatch.firstName = ehr.firstName
    mergePatch.middleName = ehr.middleName || null
    mergePatch.lastName = ehr.lastName
  }

  const scalarFields = ['dateOfBirth', 'sex', 'mrn'] as const
  for (const field of scalarFields) {
    const savedValue = saved[field]
    const ehrValue = ehr[field]
    if (ehrValue && ehrValue !== savedValue) {
      changedFields.push({
        field,
        label: EHR_DEMOGRAPHICS_FIELD_LABELS[field],
        savedValue,
        ehrValue,
      })
      // Only drifted (non-blank) scalars enter the patch, so a blank EHR value
      // never overwrites a good saved value.
      mergePatch[field] = ehrValue
    }
  }

  return {
    changedFields,
    hasDrift: changedFields.length > 0,
    ehr,
    mergePatch,
  }
}
